import React, { useCallback } from 'react';
import { Table, Tag, Space, Button, Tooltip, Image, Typography, Row, Col, Pagination, Dropdown, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, EyeOutlined, PictureOutlined, SyncOutlined, CaretDownFilled } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { viewSkuItemDetail, editSkuItem, deleteSkuItem, onChangePagination } from '../actions';
import '../SkuItems.css';

const { Text } = Typography;

const SkuItemsList = ({ skuItems, isLoading, pagination, onSyncClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleViewDetail = (id) => {
    dispatch(viewSkuItemDetail(id, navigate));
  };

  const handleEdit = (id) => {
    dispatch(editSkuItem(id, navigate));
  };

  const handleDelete = async (id) => {
    await dispatch(deleteSkuItem(id));
  };

  const handleTableChange = (pagination) => {
    dispatch(onChangePagination(pagination.current, pagination.pageSize));
  };

  // Sync menu items giống Orders
  const syncMenuItems = [
    {
      key: 'amazon',
      label: 'Sync SKU items from Amazon',
      icon: <SyncOutlined />
    }
  ];

  const handleSyncMenuClick = ({ key }) => {
    if (key === 'amazon' && onSyncClick) {
      onSyncClick();
    }
  };

  const getStatusTag = (status) => {
    let color = 'default';
    let text = status;

    switch (status) {
      case 'ACTIVE':
        color = 'green';
        text = 'Active';
        break;
      case 'INACTIVE':
        color = 'orange';
        text = 'Inactive';
        break;
      case 'DELETED':
        color = 'red';
        text = 'Deleted';
        break;
      default:
        color = 'default';
    }

    return <Tag color={color}>{text}</Tag>;
  };

  const getSellerAccountText = (item) => {
    // Ưu tiên sử dụng thông tin từ sellerAccount relation
    if (item.sellerAccount && item.sellerAccount.name) {
      return item.sellerAccount.name;
    }
    
    // Fallback: sử dụng field sellerAccount trực tiếp (legacy)
    if (item.sellerAccount) {
      return item.sellerAccount;
    }
    
    return 'Unknown';
  };

  const getMainImage = (images) => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      return null;
    }
    
    // Find the main image
    const mainImage = images.find(img => img.isMain === true);
    
    // Return the main image URL or the first image URL as fallback
    return mainImage ? mainImage.url : images[0].url;
  };

  // Function to set row className for alternating colors
  const getRowClassName = useCallback((record, index) => {
    return index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
  }, []);

  const columns = [
    {
        title: '#',
        key: 'index',
        width: 70,
        render: (_, __, index) => <span style={{fontSize:12}}>{(pagination.current - 1) * pagination.pageSize + index + 1}</span>
    },
    {
      title: <span><PictureOutlined/></span>,
      dataIndex: 'images',
      key: 'images',
      width: 80,
      align: 'center',
      render: (images) => {
        const imageUrl = getMainImage(images);
        
        return imageUrl ? (
          <Image 
            src={imageUrl} 
            className="sku-image" 
            alt="Product"
            width={35}
          />
        ) : (
          <div style={{ width: 35, height: 35, backgroundColor: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            No Image
          </div>
        );
      }
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 270,
      ellipsis: true,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      width: 150,
      render: (text) => (
        <a 
          target='_blank' 
          rel="noopener noreferrer"
          style={{ color: '#1890ff' }} 
          href={`https://www.amazon.com/dp/${text}`}
        >
          {text || '-'}
        </a>
      )
    },
    {
      title: 'Product Name',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 150,
    },
    {
      title: 'Rank',
      key: 'rank',
      width: 160,
      render: (_, record) => (
        <div>
          {record.mainRank ? (
            <div style={{ fontWeight: 600 }}>#{Number(record.mainRank).toLocaleString()}</div>
          ) : (
            <span style={{ color: '#bfbfbf' }}>-</span>
          )}
          {record.normalizedRank ? (
            <div style={{ fontSize: 12, color: '#1677ff' }}>qc: #{Number(record.normalizedRank).toLocaleString()}</div>
          ) : null}
        </div>
      )
    },
    {
      title: 'Seller Account',
      dataIndex: 'sellerAccount',
      key: 'sellerAccount',
      width: 150,
      render: (sellerAccount, record) => (
        <span className="no-line-through">{getSellerAccountText(record)}</span>
      ),
    },
    {
      title: 'Channel',
      dataIndex: 'fulfillmentChannel',
      key: 'fulfillmentChannel',
      width: 120,
      align: 'center',
      render: (channel) => (
        <span className="no-line-through">
          {channel === 'FBA' ? 
            <Tag color="orange" style={{ fontSize: 12, width: 43, marginRight: 0 }}>FBA</Tag> : 
            channel === 'FBM' ?
              <Tag color="blue" style={{ fontSize: 12, width: 43, marginRight: 0 }}>FBM</Tag> :
              <Tag color="default" style={{ fontSize: 12 }}>{channel || '-'}</Tag>
          }
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      align: 'center',
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Listing Price',
      dataIndex: 'listingPrice',
      key: 'listingPrice',
      width: 120,
      align: 'right',
      render: (price) => price != null
        ? <span style={{ color: '#389e0d', fontWeight: 500 }}>${parseFloat(price).toFixed(2)}</span>
        : <span style={{ color: '#bfbfbf' }}>—</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record.id)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete SKU item?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <Row justify="space-between" align="middle" className='skuitems-list-header' style={{ marginBottom: 16 }}>
        <Col>
          <Space direction='horizontal' size={4}>
            <Text strong style={{ fontSize: 14 }}>
              Total SKU items:
            </Text>
            <Pagination
              size="small"
              current={pagination?.current || 1}
              total={pagination?.total || 0}
              pageSize={pagination?.pageSize || 10}
              onChange={(page, pageSize) => dispatch(onChangePagination(page, pageSize))}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total}`
              }
            />
          </Space>
        </Col>
        <Col>
          <Space>
            <Dropdown
              menu={{
                items: syncMenuItems,
                onClick: handleSyncMenuClick
              }}
              trigger={['hover']}
            >
              <Button type="link">
                <SyncOutlined /> Sync <CaretDownFilled />
              </Button>
            </Dropdown>
          </Space>
        </Col>
      </Row>

      <div className="skuitems-list">
        <Table 
          columns={columns}
          dataSource={skuItems.map(item => ({ ...item, key: item.id }))}
          loading={isLoading}
          rowClassName={getRowClassName}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            size: 'small',
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} products`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
        />
      </div>
    </>
  );
};

export default SkuItemsList;
